import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/sharp-solid-svg-icons"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import FileUploaderV2 from "components/FileUploaderV2"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { DropdownObj } from "pages/Common/commonTypes"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import useAddOns, { EAddOnsFields } from "./useAddOns"

const AddOns = (props: ProjectFormPageProps) => {
    const { states, functions } = useAddOns(props)
    const { addOnsRules } = useTemplateUIRules()

    const showManpowerSection = addOnsRules.manpower_enabled !== false
    const showOMRequiredFields = addOnsRules.om_required_fields_visible !== false
    const showAmcFields = addOnsRules.is_amc !== false
    const showWarrantyFields = addOnsRules.is_warranty !== false

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={2}>
                    {/* WARRANTY SECTION */}
                    {showWarrantyFields && (
                        <>
                            <Grid size={12}>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Is Warranty Applicable?
                                    </Typography>
                                    <RadioGroup
                                        options={states.yesNoOptions}
                                        name={EAddOnsFields.IS_WARRANTY_APPLICABLE}
                                        onSelect={(value: DropdownObj) => {
                                            functions.handleRadioChange(EAddOnsFields.IS_WARRANTY_APPLICABLE, value)
                                        }}
                                        value={functions.getRadioValue(EAddOnsFields.IS_WARRANTY_APPLICABLE)}
                                    />
                                </Box>
                            </Grid>

                            {Boolean(states.formik.values[EAddOnsFields.IS_WARRANTY_APPLICABLE]) && (
                                <>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                Warranty duration (in months)*
                                            </Typography>
                                            <MuiTextField
                                                placeholder="Enter Warranty Duration"
                                                size="large"
                                                type="number"
                                                name={EAddOnsFields.WARRANTY_DURATION_IN_MONTHS}
                                                value={states.formik.values[EAddOnsFields.WARRANTY_DURATION_IN_MONTHS]}
                                                onChange={states.formik.handleChange}
                                                onBlur={states.formik.handleBlur}
                                                error={functions.hasError(EAddOnsFields.WARRANTY_DURATION_IN_MONTHS)}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                Warranty Start Criteria*
                                            </Typography>
                                            <MuiAutoComplete
                                                disablePortal={false}
                                                disableClearable
                                                options={states.warrantyStartCriteriaOptions}
                                                placeholder="Select Criteria"
                                                size="large"
                                                value={states.formik.values[EAddOnsFields.WARRANTY_START_CRITERIA]}
                                                onChange={value => {
                                                    states.formik.setFieldValue(
                                                        EAddOnsFields.WARRANTY_START_CRITERIA,
                                                        value
                                                    )
                                                }}
                                                error={functions.hasError(EAddOnsFields.WARRANTY_START_CRITERIA)}
                                            />
                                        </Box>
                                    </Grid>
                                    {states.formik.values[EAddOnsFields.WARRANTY_START_CRITERIA]?.title ===
                                        "Specific Date" && (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                    Warranty Start Date*
                                                </Typography>
                                                <MuiTextField
                                                    placeholder="Enter Date"
                                                    type="date"
                                                    size="large"
                                                    name={EAddOnsFields.WARRANTY_START_DATE}
                                                    value={states.formik.values[EAddOnsFields.WARRANTY_START_DATE]}
                                                    onChange={states.formik.handleChange}
                                                    onBlur={states.formik.handleBlur}
                                                    error={functions.hasError(EAddOnsFields.WARRANTY_START_DATE)}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* AMC SECTION */}
                    {showAmcFields && (
                        <>
                            <Grid size={12}>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Is AMC Applicable?
                                    </Typography>
                                    <RadioGroup
                                        options={states.yesNoOptions}
                                        name={EAddOnsFields.IS_AMC_APPLICABLE}
                                        onSelect={(value: DropdownObj) => {
                                            functions.handleRadioChange(EAddOnsFields.IS_AMC_APPLICABLE, value)
                                        }}
                                        value={functions.getRadioValue(EAddOnsFields.IS_AMC_APPLICABLE)}
                                    />
                                </Box>
                            </Grid>

                            {Boolean(states.formik.values[EAddOnsFields.IS_AMC_APPLICABLE]) && (
                                <>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                AMC Document*
                                            </Typography>
                                            <FileUploaderV2
                                                maxFiles={1}
                                                docIds={
                                                    states.existingAmcDocuments.length > 0
                                                        ? states.existingAmcDocuments.map(doc => doc.doc_id)
                                                        : undefined
                                                }
                                                key={`${states.existingAmcDocuments.length}-${states.project_id}`}
                                                associatedId={states.project_id}
                                                fileType={states.FILE_TYPES.AMC_DOCUMENT}
                                                onUpload={functions.handleFileUpload}
                                                initialFileCount={states.existingAmcDocuments.length}
                                                existingFileNames={states.existingAmcDocuments.map(
                                                    doc => doc.document_title
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                AMC Type*
                                            </Typography>
                                            <MuiAutoComplete
                                                disablePortal={false}
                                                disableClearable
                                                options={states.amcTypeOptions}
                                                placeholder="Select AMC Type"
                                                size="large"
                                                value={states.formik.values[EAddOnsFields.AMC_TYPE]}
                                                onChange={value => {
                                                    states.formik.setFieldValue(EAddOnsFields.AMC_TYPE, value)
                                                }}
                                                error={functions.hasError(EAddOnsFields.AMC_TYPE)}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                AMC Start Date*
                                            </Typography>
                                            <MuiTextField
                                                placeholder="Enter Date"
                                                type="date"
                                                size="large"
                                                name={EAddOnsFields.AMC_START_DATE}
                                                value={states.formik.values[EAddOnsFields.AMC_START_DATE]}
                                                onChange={states.formik.handleChange}
                                                onBlur={states.formik.handleBlur}
                                                error={functions.hasError(EAddOnsFields.AMC_START_DATE)}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                                AMC Duration (in months)*
                                            </Typography>
                                            <MuiTextField
                                                placeholder="Enter AMC Duration"
                                                type="number"
                                                size="large"
                                                name={EAddOnsFields.AMC_DURATION}
                                                value={states.formik.values[EAddOnsFields.AMC_DURATION]}
                                                onChange={states.formik.handleChange}
                                                onBlur={states.formik.handleBlur}
                                                error={functions.hasError(EAddOnsFields.AMC_DURATION)}
                                            />
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    {/* MANPOWER SECTION */}
                    {showManpowerSection && (
                        <Grid
                            size={12}
                            sx={{
                                paddingTop: "12px",
                            }}>
                            <Box display={"flex"} flexDirection={"column"} gap={2}>
                                <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                                    MANPOWER
                                </Typography>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Is Manpower To be Deployed?*
                                    </Typography>
                                    <RadioGroup
                                        options={states.yesNoOptions}
                                        name={EAddOnsFields.IS_MANPOWER_APPLICABLE}
                                        onSelect={(value: DropdownObj) => {
                                            functions.handleRadioChange(EAddOnsFields.IS_MANPOWER_APPLICABLE, value)
                                        }}
                                        value={functions.getRadioValue(EAddOnsFields.IS_MANPOWER_APPLICABLE)}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {showManpowerSection && Boolean(states.formik.values[EAddOnsFields.IS_MANPOWER_APPLICABLE]) && (
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                {/* Header Row */}
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Typography
                                            fontSize={"14px"}
                                            variant="label"
                                            fontWeight={500}
                                            display={"flex"}
                                            alignItems={"center"}
                                            gap={"4px"}>
                                            Manpower Type*
                                            {functions.hasManpowerTypeError() && (
                                                <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" />
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {/* Marshal Row */}
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display="flex" alignItems="center">
                                            <MuiCheckbox
                                                checked={states.formik.values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED]}
                                                onChange={e =>
                                                    states.formik.setFieldValue(
                                                        EAddOnsFields.MANPOWER_MARSHAL_ENABLED,
                                                        e.target.checked
                                                    )
                                                }
                                                sx={{ padding: 0, marginRight: 1 }}
                                            />
                                            <Typography fontSize={"13px"} variant="caption" textTransform={"none"}>
                                                Marshal
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Number of Resources"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_MARSHAL_RESOURCE}
                                            value={states.formik.values[EAddOnsFields.MANPOWER_MARSHAL_RESOURCE]}
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={!states.formik.values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED]}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Duration (In years)"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_MARSHAL_DURATION}
                                            value={states.formik.values[EAddOnsFields.MANPOWER_MARSHAL_DURATION]}
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={!states.formik.values[EAddOnsFields.MANPOWER_MARSHAL_ENABLED]}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Guard Row */}
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display="flex" alignItems="center">
                                            <MuiCheckbox
                                                checked={states.formik.values[EAddOnsFields.MANPOWER_GUARD_ENABLED]}
                                                onChange={e =>
                                                    states.formik.setFieldValue(
                                                        EAddOnsFields.MANPOWER_GUARD_ENABLED,
                                                        e.target.checked
                                                    )
                                                }
                                                sx={{ padding: 0, marginRight: 1 }}
                                            />
                                            <Typography fontSize={"13px"} variant="caption" textTransform={"none"}>
                                                Guard
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Number of Resources"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_GUARD_RESOURCE}
                                            value={states.formik.values[EAddOnsFields.MANPOWER_GUARD_RESOURCE]}
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={!states.formik.values[EAddOnsFields.MANPOWER_GUARD_ENABLED]}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Duration (In years)"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_GUARD_DURATION}
                                            value={states.formik.values[EAddOnsFields.MANPOWER_GUARD_DURATION]}
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={!states.formik.values[EAddOnsFields.MANPOWER_GUARD_ENABLED]}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Customer Support Row */}
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display="flex" alignItems="center">
                                            <MuiCheckbox
                                                checked={
                                                    states.formik.values[
                                                        EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED
                                                    ]
                                                }
                                                onChange={e =>
                                                    states.formik.setFieldValue(
                                                        EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED,
                                                        e.target.checked
                                                    )
                                                }
                                                sx={{ padding: 0, marginRight: 1 }}
                                            />
                                            <Typography
                                                fontSize={"13px"}
                                                variant="caption"
                                                textTransform={"none"}
                                                noWrap>
                                                Customer Support
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Number of Resources"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE}
                                            value={
                                                states.formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_RESOURCE]
                                            }
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={
                                                !states.formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]
                                            }
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <MuiTextField
                                            placeholder="Duration (In years)"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION}
                                            value={
                                                states.formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_DURATION]
                                            }
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            disabled={
                                                !states.formik.values[EAddOnsFields.MANPOWER_CUSTOMER_SUPPORT_ENABLED]
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    )}

                    {/* CHARGER CONNECTIVITY SECTION */}
                    <Grid
                        size={12}
                        sx={{
                            paddingTop: "12px",
                        }}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            CHARGER CONNECTIVITY
                        </Typography>
                    </Grid>

                    <Grid size={12}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Charger Connectivity Required?
                            </Typography>
                            <RadioGroup
                                options={states.yesNoOptions}
                                name={EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED}
                                onSelect={(value: DropdownObj) => {
                                    functions.handleRadioChange(EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED, value)
                                }}
                                value={functions.getRadioValue(EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED)}
                            />
                        </Box>
                    </Grid>

                    {Boolean(states.formik.values[EAddOnsFields.CHARGER_CONNECTIVITY_REQUIRED]) && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Charger Connection Type*
                                    </Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        options={states.chargerConnectionTypeOptions}
                                        placeholder="Select Charger Network"
                                        size="large"
                                        value={states.formik.values[EAddOnsFields.CHARGER_CONNECTION_TYPE]}
                                        onChange={value => {
                                            states.formik.setFieldValue(EAddOnsFields.CHARGER_CONNECTION_TYPE, value)
                                        }}
                                        error={functions.hasError(EAddOnsFields.CHARGER_CONNECTION_TYPE)}
                                    />
                                </Box>
                            </Grid>
                            {states.formik.values[EAddOnsFields.CHARGER_CONNECTION_TYPE]?.title === "SIM" && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box display={"flex"} flexDirection={"column"} gap={1}>
                                        <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                            SIM Duration (in months)*
                                        </Typography>
                                        <MuiTextField
                                            placeholder="Enter SIM Duration"
                                            size="large"
                                            type="number"
                                            name={EAddOnsFields.SIM_DURATION}
                                            value={states.formik.values[EAddOnsFields.SIM_DURATION]}
                                            onChange={states.formik.handleChange}
                                            onBlur={states.formik.handleBlur}
                                            error={functions.hasError(EAddOnsFields.SIM_DURATION)}
                                        />
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* O&M AND INSURANCE SECTION */}
                    {showOMRequiredFields && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    O&M opted*
                                </Typography>
                                <RadioGroup
                                    options={states.yesNoOptions}
                                    name={EAddOnsFields.IS_O_AND_M_REQUIRED}
                                    onSelect={(value: DropdownObj) => {
                                        functions.handleRadioChange(EAddOnsFields.IS_O_AND_M_REQUIRED, value)
                                    }}
                                    value={functions.getRadioValue(EAddOnsFields.IS_O_AND_M_REQUIRED)}
                                />
                            </Box>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Insurance Required*
                            </Typography>
                            <RadioGroup
                                options={states.yesNoOptions}
                                name={EAddOnsFields.INSURANCE_REQUIRED}
                                onSelect={(value: DropdownObj) => {
                                    functions.handleRadioChange(EAddOnsFields.INSURANCE_REQUIRED, value)
                                }}
                                value={functions.getRadioValue(EAddOnsFields.INSURANCE_REQUIRED)}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ProjectFormFooter
                isLoading={states.isLoading}
                handleCloseForm={props.handleCloseForm}
                handleSubmit={() => states.formik.handleSubmit()}
            />
        </>
    )
}

export default AddOns
